import React, { useMemo, useState } from "react";

function ProduzioneDaFatturare() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [database, setDatabase] = useState("");

  const [hiddenColumns, setHiddenColumns] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    NUMERO_FATTURA: "",
    DATA_FATTURA: "",
    TESTATA: "",
    IMPONIBILE_TOT: "",
    IVA: "",
    RA: "",
  });

  const HIDDEN_SYSTEM_COLUMNS = ["ID"];

  const fetchData = async () => {
    if (!database) {
      alert('Seleziona prima il database "CED" o "CML"');
      return;
    }

    try {
      setLoading(true);
      setError("");
      setRows([]);
      setSelectedRowKeys([]);
      setShowInvoiceForm(false);

      const token = localStorage.getItem("token");

      const response = await fetch(
        "https://api.lapegna.xyz/produzione/da_fatturare",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            NomeDatabase: database,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Errore API ${response.status}`);
      }

      const data = await response.json();

      let normalizedData = [];

      if (Array.isArray(data)) {
        normalizedData = data;
      } else if (Array.isArray(data?.data)) {
        normalizedData = data.data;
      } else if (data && typeof data === "object") {
        normalizedData = [data];
      }

      setRows(normalizedData);
      setColumnFilters({});
      setHiddenColumns({});
      setSortConfig({ key: null, direction: "asc" });
    } catch (err) {
      console.error(err);
      setError("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const getRowKey = (row, rowIndex) => {
    if (row?.ID !== undefined && row?.ID !== null) {
      return String(row.ID);
    }

    return `${row?.CLIENTE ?? "NOCLIENTE"}_${row?.DATA_PRODUZIONE ?? "NODATA"}_${rowIndex}`;
  };

  const formatItalianDate = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  };

  const formatInputDate = (value) => {
    if (!value) {
      return new Date().toISOString().split("T")[0];
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return new Date().toISOString().split("T")[0];
    }

    return date.toISOString().split("T")[0];
  };

  const parseNumericValue = (value) => {
    if (typeof value === "number") return value;

    if (typeof value === "string") {
      const trimmed = value.trim();

      if (!trimmed) return 0;

      if (trimmed.includes(",") && trimmed.includes(".")) {
        const normalized = trimmed.replace(/\./g, "").replace(",", ".");
        const parsed = Number(normalized);
        return Number.isNaN(parsed) ? 0 : parsed;
      }

      if (trimmed.includes(",")) {
        const normalized = trimmed.replace(",", ".");
        const parsed = Number(normalized);
        return Number.isNaN(parsed) ? 0 : parsed;
      }

      const parsed = Number(trimmed);
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    return 0;
  };

  const formatEuro = (value) => {
    const numericValue = parseNumericValue(value);

    return `${numericValue.toLocaleString("it-IT", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} €`;
  };

  const getColumnLabel = (column) => {
    if (column === "DATA_PRODUZIONE") return "Data Produzione";
    return column;
  };

  const allColumns = useMemo(() => {
    const set = new Set();

    rows.forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (!HIDDEN_SYSTEM_COLUMNS.includes(key)) {
          set.add(key);
        }
      });
    });

    return Array.from(set);
  }, [rows]);

  const visibleColumns = useMemo(() => {
    return allColumns.filter((col) => !hiddenColumns[col]);
  }, [allColumns, hiddenColumns]);

  const isNumericValue = (value) => {
    if (value === null || value === undefined || value === "") return false;

    if (typeof value === "number") return Number.isFinite(value);

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return false;

      const normalized = trimmed.includes(",")
        ? trimmed.replace(/\./g, "").replace(",", ".")
        : trimmed;

      return !Number.isNaN(Number(normalized));
    }

    return false;
  };

  const columnIsNumeric = (column) => {
    const nonEmptyValues = rows
      .map((row) => row?.[column])
      .filter((value) => value !== null && value !== undefined && value !== "");

    if (nonEmptyValues.length === 0) return false;

    return nonEmptyValues.every(isNumericValue);
  };

  const normalizedCellValueForFilter = (column, value) => {
    if (value === null || value === undefined) return "";

    if (column === "DATA_PRODUZIONE") {
      return formatItalianDate(value).toLowerCase();
    }

    if (column === "IMPORTO" || column === "CORRISPETTIVO") {
      return formatEuro(value).toLowerCase();
    }

    return String(value).toLowerCase();
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) =>
      allColumns.every((column) => {
        const filterValue = (columnFilters[column] || "").trim().toLowerCase();

        if (!filterValue) return true;

        const cellValue = row?.[column];
        return normalizedCellValueForFilter(column, cellValue).includes(filterValue);
      })
    );
  }, [rows, allColumns, columnFilters]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];

    if (!sortConfig.key) return sorted;

    sorted.sort((a, b) => {
      const aValue = a?.[sortConfig.key];
      const bValue = b?.[sortConfig.key];

      const aEmpty = aValue === null || aValue === undefined || aValue === "";
      const bEmpty = bValue === null || bValue === undefined || bValue === "";

      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      let result = 0;

      if (sortConfig.key === "DATA_PRODUZIONE") {
        result = new Date(aValue).getTime() - new Date(bValue).getTime();
      } else {
        const bothNumeric = isNumericValue(aValue) && isNumericValue(bValue);

        if (bothNumeric) {
          result = parseNumericValue(aValue) - parseNumericValue(bValue);
        } else {
          result = String(aValue).localeCompare(String(bValue), "it", {
            sensitivity: "base",
            numeric: true,
          });
        }
      }

      return sortConfig.direction === "asc" ? result : -result;
    });

    return sorted;
  }, [filteredRows, sortConfig]);

  const totals = useMemo(() => {
    const result = {};

    visibleColumns.forEach((column) => {
      if (!columnIsNumeric(column)) {
        result[column] = null;
        return;
      }

      const sum = sortedRows.reduce((acc, row) => {
        const value = row?.[column];
        return acc + (isNumericValue(value) ? parseNumericValue(value) : 0);
      }, 0);

      result[column] = sum;
    });

    return result;
  }, [sortedRows, visibleColumns, rows]);

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.key !== column) {
        return { key: column, direction: "asc" };
      }

      if (prev.direction === "asc") {
        return { key: column, direction: "desc" };
      }

      return { key: null, direction: "asc" };
    });
  };

  const getSortArrow = (column) => {
    if (sortConfig.key !== column) return "↕";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  const toggleColumnVisibility = (column) => {
    setHiddenColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const handleFilterChange = (column, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [column]: value,
    }));
  };

  const clearFilters = () => {
    setColumnFilters({});
  };

  const formatCellValue = (column, value) => {
    if (value === null || value === undefined) return "";

    if (column === "DATA_PRODUZIONE") {
      return formatItalianDate(value);
    }

    if (column === "IMPORTO" || column === "CORRISPETTIVO") {
      return formatEuro(value);
    }

    if (typeof value === "object") return JSON.stringify(value);

    return String(value);
  };

  const formatDisplayedCellValue = (column, value) => {
    const fullValue = formatCellValue(column, value);

    if (column === "DESCRIZIONE") {
      if (fullValue.length <= 100) return fullValue;
      return `${fullValue.slice(0, 100)}...`;
    }

    return fullValue;
  };

  const formatTotalValue = (column, value) => {
    if (value === null || value === undefined) return "";

    if (column === "IMPORTO" || column === "CORRISPETTIVO") {
      return formatEuro(value);
    }

    return new Intl.NumberFormat("it-IT", {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const escapeCsvValue = (value) => {
    const stringValue =
      value === null || value === undefined ? "" : String(value);
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  const exportToCsv = () => {
    if (sortedRows.length === 0) {
      alert("Nessun dato da esportare");
      return;
    }

    const header = visibleColumns
      .map((column) => escapeCsvValue(getColumnLabel(column)))
      .join(";");

    const body = sortedRows
      .map((row) =>
        visibleColumns
          .map((column) => {
            let value = row?.[column] ?? "";

            if (column === "DATA_PRODUZIONE") value = formatItalianDate(value);
            if (column === "IMPORTO" || column === "CORRISPETTIVO") value = formatEuro(value);

            return escapeCsvValue(value);
          })
          .join(";")
      )
      .join("\n");

    const csvContent = `${header}\n${body}`;

    const blob = new Blob([`\uFEFF${csvContent}`], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `produzione_da_fatturare_${database || "database"}.csv`
    );

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const selectedRows = useMemo(() => {
    const selectedSet = new Set(selectedRowKeys);
    return sortedRows.filter((row, index) => selectedSet.has(getRowKey(row, index)));
  }, [sortedRows, selectedRowKeys]);

  const selectedCliente = selectedRows.length > 0 ? selectedRows[0]?.CLIENTE : null;

  const selectedImponibile = useMemo(() => {
    return selectedRows.reduce((acc, row) => {
      const rawValue =
        row?.CORRISPETTIVO !== undefined && row?.CORRISPETTIVO !== null
          ? row.CORRISPETTIVO
          : row?.IMPORTO;
      return acc + parseNumericValue(rawValue);
    }, 0);
  }, [selectedRows]);

  const selectedIva = useMemo(() => {
    return selectedImponibile * 0.22;
  }, [selectedImponibile]);

  const selectedDescriptions = useMemo(() => {
    const list = selectedRows
      .map((row) => String(row?.DESCRIZIONE ?? "").trim())
      .filter(Boolean);

    return list.join("\n");
  }, [selectedRows]);

  const openInvoiceForm = () => {
    setInvoiceForm({
      NUMERO_FATTURA: "",
      DATA_FATTURA: new Date().toISOString().split("T")[0],
      TESTATA: selectedDescriptions,
      IMPONIBILE_TOT: selectedImponibile.toFixed(2),
      IVA: selectedIva.toFixed(2),
      RA: "",
    });
    setShowInvoiceForm(true);
  };

  const handleInvoiceFormChange = (field, value) => {
    setInvoiceForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isRowSelected = (row, rowIndex) => {
    return selectedRowKeys.includes(getRowKey(row, rowIndex));
  };

  const isRowSelectable = (row) => {
    if (!selectedCliente) return true;
    return row?.CLIENTE === selectedCliente;
  };

  const handleRowCheckboxChange = (row, rowIndex) => {
    const rowKey = getRowKey(row, rowIndex);
    const checked = selectedRowKeys.includes(rowKey);

    if (checked) {
      setSelectedRowKeys((prev) => prev.filter((key) => key !== rowKey));
      return;
    }

    if (!isRowSelectable(row)) {
      return;
    }

    setSelectedRowKeys((prev) => [...prev, rowKey]);
  };

  const areAllSelectableRowsChecked = useMemo(() => {
    const selectableRows = sortedRows.filter((row) => {
      if (!selectedCliente) return true;
      return row?.CLIENTE === selectedCliente;
    });

    if (selectableRows.length === 0) return false;

    return selectableRows.every((row, index) =>
      selectedRowKeys.includes(getRowKey(row, index))
    );
  }, [sortedRows, selectedRowKeys, selectedCliente]);

  const handleSelectAllChange = () => {
    const selectableKeys = sortedRows
      .filter((row) => {
        if (!selectedCliente) return true;
        return row?.CLIENTE === selectedCliente;
      })
      .map((row, index) => getRowKey(row, index));

    if (selectableKeys.length === 0) return;

    if (areAllSelectableRowsChecked) {
      setSelectedRowKeys((prev) =>
        prev.filter((key) => !selectableKeys.includes(key))
      );
      return;
    }

    if (!selectedCliente && sortedRows.length > 0) {
      const firstCliente = sortedRows[0]?.CLIENTE;
      const sameClienteKeys = sortedRows
        .filter((row) => row?.CLIENTE === firstCliente)
        .map((row, index) => getRowKey(row, index));

      setSelectedRowKeys(sameClienteKeys);
      return;
    }

    setSelectedRowKeys(selectableKeys);
  };

  return (
    <div
      style={{
        padding: 20,
        color: "white",
        minHeight: "100%",
        background: "transparent",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>
            Produzione da fatturare
          </h2>
          <div style={{ opacity: 0.8, marginTop: 6 }}>
            Vista gestionale con filtri, ordinamento, totali ed export CSV
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {selectedRowKeys.length > 0 && (
            <button
              onClick={openInvoiceForm}
              style={{
                padding: "10px 16px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.25)",
                background: "rgba(168,85,247,0.24)",
                color: "white",
                cursor: "pointer",
                fontWeight: 600,
                backdropFilter: "blur(6px)",
              }}
            >
              Compila fattura
            </button>
          )}

          <button
            onClick={exportToCsv}
            disabled={sortedRows.length === 0}
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.25)",
              background:
                sortedRows.length === 0
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(34,197,94,0.18)",
              color: "white",
              cursor: sortedRows.length === 0 ? "not-allowed" : "pointer",
              fontWeight: 600,
              backdropFilter: "blur(6px)",
            }}
          >
            Export CSV
          </button>
        </div>
      </div>

      <div
        style={{
          marginBottom: 20,
          padding: 16,
          border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: 14,
          background: "rgba(255,255,255,0.05)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "end",
            flexWrap: "wrap",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontWeight: 600,
              }}
            >
              Database
            </label>
            <select
              value={database}
              onChange={(e) => setDatabase(e.target.value)}
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.25)",
                background: "rgba(255,255,255,0.08)",
                color: "white",
                minWidth: 180,
                outline: "none",
              }}
            >
              <option value="" style={{ color: "black" }}>
                Seleziona database
              </option>
              <option value="CED" style={{ color: "black" }}>
                CED
              </option>
              <option value="CML" style={{ color: "black" }}>
                CML
              </option>
            </select>
          </div>

          <button
            onClick={fetchData}
            disabled={loading}
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.25)",
              background: "rgba(59,130,246,0.22)",
              color: "white",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: 600,
              backdropFilter: "blur(6px)",
            }}
          >
            {loading ? "Caricamento..." : "Carica dati"}
          </button>

          <button
            onClick={clearFilters}
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.25)",
              background: "rgba(255,255,255,0.08)",
              color: "white",
              cursor: "pointer",
              fontWeight: 600,
              backdropFilter: "blur(6px)",
            }}
          >
            Pulisci filtri
          </button>
        </div>
      </div>

      <div
        style={{
          marginBottom: 20,
          padding: 16,
          border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: 14,
          background: "rgba(255,255,255,0.05)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 12 }}>
          Mostra / nascondi colonne
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 10,
          }}
        >
          {allColumns.length === 0 ? (
            <div style={{ opacity: 0.8 }}>Nessuna colonna disponibile</div>
          ) : (
            allColumns.map((column) => (
              <label
                key={column}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={!hiddenColumns[column]}
                  onChange={() => toggleColumnVisibility(column)}
                />
                <span>{getColumnLabel(column)}</span>
              </label>
            ))
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: 20,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <div
          style={{
            padding: 14,
            borderRadius: 14,
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.18)",
            minWidth: 180,
            backdropFilter: "blur(10px)",
          }}
        >
          <div style={{ opacity: 0.8, fontSize: 13 }}>Record totali</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{rows.length}</div>
        </div>

        <div
          style={{
            padding: 14,
            borderRadius: 14,
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.18)",
            minWidth: 180,
            backdropFilter: "blur(10px)",
          }}
        >
          <div style={{ opacity: 0.8, fontSize: 13 }}>Record filtrati</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{sortedRows.length}</div>
        </div>

        <div
          style={{
            padding: 14,
            borderRadius: 14,
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.18)",
            minWidth: 180,
            backdropFilter: "blur(10px)",
          }}
        >
          <div style={{ opacity: 0.8, fontSize: 13 }}>Colonne visibili</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>
            {visibleColumns.length}
          </div>
        </div>

        <div
          style={{
            padding: 14,
            borderRadius: 14,
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.18)",
            minWidth: 220,
            backdropFilter: "blur(10px)",
          }}
        >
          <div style={{ opacity: 0.8, fontSize: 13 }}>Righe selezionate</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>
            {selectedRowKeys.length}
          </div>
          {selectedCliente && (
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
              Cliente selezionato: {selectedCliente}
            </div>
          )}
        </div>

        <div
          style={{
            padding: 14,
            borderRadius: 14,
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.18)",
            minWidth: 220,
            backdropFilter: "blur(10px)",
          }}
        >
          <div style={{ opacity: 0.8, fontSize: 13 }}>Imponibile selezionato</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>
            {formatEuro(selectedImponibile)}
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 10,
            color: "#fecaca",
            background: "rgba(127,29,29,0.35)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div
          style={{
            padding: 18,
            borderRadius: 14,
            border: "1px solid rgba(255,255,255,0.18)",
            background: "rgba(255,255,255,0.05)",
          }}
        >
          Nessun dato caricato.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div
          style={{
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: 16,
            overflow: "hidden",
            background: "rgba(255,255,255,0.04)",
            backdropFilter: "blur(12px)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "transparent",
                color: "white",
                minWidth: 1200,
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "rgba(255,255,255,0.08)",
                  }}
                >
                  <th
                    style={{
                      borderBottom: "1px solid rgba(255,255,255,0.18)",
                      borderRight: "1px solid rgba(255,255,255,0.08)",
                      padding: "10px 12px",
                      width: 60,
                      minWidth: 60,
                      textAlign: "center",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={areAllSelectableRowsChecked}
                      onChange={handleSelectAllChange}
                    />
                  </th>

                  {visibleColumns.map((column) => (
                    <th
                      key={column}
                      style={{
                        borderBottom: "1px solid rgba(255,255,255,0.18)",
                        borderRight: "1px solid rgba(255,255,255,0.08)",
                        padding: 0,
                        verticalAlign: "top",
                        minWidth: column === "DESCRIZIONE" ? 260 : 160,
                      }}
                    >
                      <div
                        style={{
                          padding: "10px 12px 8px 12px",
                        }}
                      >
                        <button
                          onClick={() => handleSort(column)}
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            background: "transparent",
                            border: "none",
                            color: "white",
                            cursor: "pointer",
                            padding: 0,
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                          title="Ordina colonna"
                        >
                          <span>{getColumnLabel(column)}</span>
                          <span style={{ opacity: 0.9 }}>{getSortArrow(column)}</span>
                        </button>

                        <input
                          type="text"
                          value={columnFilters[column] || ""}
                          onChange={(e) =>
                            handleFilterChange(column, e.target.value)
                          }
                          placeholder="Filtra..."
                          style={{
                            width: "100%",
                            marginTop: 10,
                            padding: "8px 10px",
                            borderRadius: 8,
                            border: "1px solid rgba(255,255,255,0.18)",
                            background: "rgba(255,255,255,0.06)",
                            color: "white",
                            outline: "none",
                            fontSize: 12,
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {sortedRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleColumns.length + 1}
                      style={{
                        padding: 18,
                        textAlign: "center",
                        borderBottom: "1px solid rgba(255,255,255,0.12)",
                      }}
                    >
                      Nessun record trovato con i filtri impostati.
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((row, rowIndex) => {
                    const selected = isRowSelected(row, rowIndex);
                    const selectable = isRowSelectable(row);

                    return (
                      <tr
                        key={getRowKey(row, rowIndex)}
                        style={{
                          background: selected
                            ? "rgba(59,130,246,0.22)"
                            : rowIndex % 2 === 0
                            ? "rgba(255,255,255,0.015)"
                            : "rgba(255,255,255,0.035)",
                          transition: "background 0.2s ease",
                          opacity: selectable || selected ? 1 : 0.45,
                        }}
                        onMouseEnter={(e) => {
                          if (!selected) {
                            e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = selected
                            ? "rgba(59,130,246,0.22)"
                            : rowIndex % 2 === 0
                            ? "rgba(255,255,255,0.015)"
                            : "rgba(255,255,255,0.035)";
                        }}
                      >
                        <td
                          style={{
                            padding: "11px 12px",
                            borderBottom: "1px solid rgba(255,255,255,0.10)",
                            borderRight: "1px solid rgba(255,255,255,0.06)",
                            textAlign: "center",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={!selectable && !selected}
                            onChange={() => handleRowCheckboxChange(row, rowIndex)}
                          />
                        </td>

                        {visibleColumns.map((column) => {
                          const fullValue = formatCellValue(column, row?.[column]);
                          const displayedValue = formatDisplayedCellValue(
                            column,
                            row?.[column]
                          );

                          return (
                            <td
                              key={`${getRowKey(row, rowIndex)}-${column}`}
                              title={column === "DESCRIZIONE" ? fullValue : undefined}
                              style={{
                                padding: "11px 12px",
                                borderBottom: "1px solid rgba(255,255,255,0.10)",
                                borderRight: "1px solid rgba(255,255,255,0.06)",
                                whiteSpace:
                                  column === "DESCRIZIONE" ? "normal" : "nowrap",
                                fontSize: 13,
                                maxWidth: column === "DESCRIZIONE" ? 420 : "none",
                                wordBreak: "break-word",
                                textAlign:
                                  column === "IMPORTO" || column === "CORRISPETTIVO"
                                    ? "right"
                                    : "left",
                                fontVariantNumeric:
                                  column === "IMPORTO" || column === "CORRISPETTIVO"
                                    ? "tabular-nums"
                                    : "normal",
                              }}
                            >
                              {displayedValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>

              {visibleColumns.length > 0 && sortedRows.length > 0 && (
                <tfoot>
                  <tr
                    style={{
                      background: "rgba(255,255,255,0.10)",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px",
                        borderTop: "1px solid rgba(255,255,255,0.18)",
                        borderRight: "1px solid rgba(255,255,255,0.08)",
                        fontWeight: 700,
                        textAlign: "center",
                      }}
                    >
                      Tot.
                    </td>

                    {visibleColumns.map((column, index) => (
                      <td
                        key={`total-${column}`}
                        style={{
                          padding: "12px",
                          borderTop: "1px solid rgba(255,255,255,0.18)",
                          borderRight: "1px solid rgba(255,255,255,0.08)",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          textAlign:
                            column === "IMPORTO" || column === "CORRISPETTIVO"
                              ? "right"
                              : "left",
                        }}
                      >
                        {index === 0
                          ? totals[column] !== null
                            ? `Totale: ${formatTotalValue(column, totals[column])}`
                            : "Totali"
                          : totals[column] !== null
                          ? formatTotalValue(column, totals[column])
                          : ""}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {showInvoiceForm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 900,
              maxHeight: "90vh",
              overflowY: "auto",
              borderRadius: 18,
              border: "1px solid rgba(255,255,255,0.18)",
              background: "rgba(17,24,39,0.92)",
              backdropFilter: "blur(14px)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
              padding: 24,
              color: "white",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                marginBottom: 20,
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: 24 }}>Compila fattura</h3>
                <div style={{ opacity: 0.75, marginTop: 6 }}>
                  Cliente: {selectedCliente || "-"} · Righe selezionate: {selectedRows.length}
                </div>
              </div>

              <button
                onClick={() => setShowInvoiceForm(false)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(255,255,255,0.08)",
                  color: "white",
                  cursor: "pointer",
                }}
              >
                Chiudi
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 16,
              }}
            >
              <div>
                <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                  NUMERO_FATTURA
                </label>
                <input
                  type="text"
                  value={invoiceForm.NUMERO_FATTURA}
                  onChange={(e) =>
                    handleInvoiceFormChange("NUMERO_FATTURA", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,255,255,0.06)",
                    color: "white",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                  DATA_FATTURA
                </label>
                <input
                  type="date"
                  value={invoiceForm.DATA_FATTURA}
                  onChange={(e) =>
                    handleInvoiceFormChange("DATA_FATTURA", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,255,255,0.06)",
                    color: "white",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                  IMPONIBILE_TOT
                </label>
                <input
                  type="text"
                  value={invoiceForm.IMPONIBILE_TOT}
                  onChange={(e) =>
                    handleInvoiceFormChange("IMPONIBILE_TOT", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,255,255,0.06)",
                    color: "white",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                  IVA
                </label>
                <input
                  type="text"
                  value={invoiceForm.IVA}
                  onChange={(e) => handleInvoiceFormChange("IVA", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,255,255,0.06)",
                    color: "white",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                  RA
                </label>
                <input
                  type="text"
                  value={invoiceForm.RA}
                  onChange={(e) => handleInvoiceFormChange("RA", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.18)",
                    background: "rgba(255,255,255,0.06)",
                    color: "white",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
                TESTATA
              </label>
              <textarea
                value={invoiceForm.TESTATA}
                onChange={(e) => handleInvoiceFormChange("TESTATA", e.target.value)}
                rows={8}
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.06)",
                  color: "white",
                  boxSizing: "border-box",
                  resize: "vertical",
                }}
              />
            </div>

            <div
              style={{
                marginTop: 20,
                padding: 14,
                borderRadius: 12,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.12)",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <div>
                <div style={{ opacity: 0.75, fontSize: 12 }}>Imponibile calcolato</div>
                <div style={{ fontWeight: 700 }}>{formatEuro(selectedImponibile)}</div>
              </div>

              <div>
                <div style={{ opacity: 0.75, fontSize: 12 }}>IVA calcolata 22%</div>
                <div style={{ fontWeight: 700 }}>{formatEuro(selectedIva)}</div>
              </div>

              <div>
                <div style={{ opacity: 0.75, fontSize: 12 }}>Data proposta</div>
                <div style={{ fontWeight: 700 }}>
                  {formatItalianDate(invoiceForm.DATA_FATTURA || formatInputDate())}
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 22,
              }}
            >
              <button
                onClick={() => setShowInvoiceForm(false)}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(255,255,255,0.08)",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Annulla
              </button>

              <button
                onClick={() => {
                  console.log("Dati fattura:", invoiceForm);
                  alert("Form compilato. Ora puoi collegarlo alla tua API.");
                }}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(34,197,94,0.2)",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProduzioneDaFatturare;